import { useCallback, useEffect, useState } from 'react';
import NavBar from './nav_bar.js';
import BackButton from './BackButton.js';
import './index.css';

// /requests — staff view of paid song requests, ranked per artist by how many
// people paid to request each song. Gated by the same code as /scan, kept on
// the device so it's entered once.
const CODE_KEY = 'mp3j_scan_code';

function readStoredCode() {
  try { return localStorage.getItem(CODE_KEY) || ''; } catch { return ''; }
}

function RequestsPage() {
  const [code, setCode] = useState(readStoredCode);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);

  const persistCode = (value) => {
    setCode(value);
    try {
      if (value) localStorage.setItem(CODE_KEY, value);
      else localStorage.removeItem(CODE_KEY);
    } catch { /* storage blocked — in-memory only */ }
  };

  const load = useCallback(async () => {
    if (!code.trim()) { setError('Enter the staff code first.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/requests/data?code=${encodeURIComponent(code.trim())}`);
      const body = await res.json().catch(() => ({}));
      if (res.status === 403) { setError('Wrong staff code.'); return; }
      if (res.status === 429) { setError(body.msg || 'Too many attempts — wait a bit.'); return; }
      if (!res.ok) { setError(body.msg || 'Could not load requests.'); return; }
      setData(body);
      setLoadedOnce(true);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [code]);

  // auto-load if a code is already saved on this device
  useEffect(() => {
    if (readStoredCode()) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <NavBar />
      <main className='page'>
        <div className='page-content'>
          <BackButton fallback='/' />
          <div className='card'>
            <h1>Song requests</h1>
            <p className='help-text'>Ranked by how many people paid to request each song.</p>
            <div className='field'>
              <label className='label' htmlFor='req-code'>Staff code</label>
              <input
                id='req-code'
                className='input'
                type='password'
                autoComplete='off'
                value={code}
                onChange={(e) => persistCode(e.target.value)}
              />
            </div>
            <button type='button' className='btn btn-primary' onClick={load} disabled={loading}>
              {loading ? 'Loading…' : loadedOnce ? 'Refresh' : 'Load requests'}
            </button>
            {error && <p className='error-text'>{error}</p>}
            {data?.configured === false && (
              <p className='help-text'>Song requests aren't configured yet (no Stripe price set).</p>
            )}
          </div>

          {data?.artists?.map((a) => (
            <div className='card' key={a.artist}>
              <h2>{a.name}</h2>
              <p className='help-text'>
                {a.totalPaid} paid{a.noSongCount ? ` · ${a.noSongCount} with no song` : ''}
              </p>
              {a.songs.length === 0 ? (
                <p className='empty-state'>No song requests yet.</p>
              ) : (
                <ol className='request-list'>
                  {a.songs.map((s, i) => (
                    <li key={`${a.artist}-${i}`} className='request-row'>
                      <span className='request-count'>{s.count}×</span>
                      <span className='request-song'>
                        {s.link ? (
                          <a href={s.link} target='_blank' rel='noopener noreferrer'>{s.title}</a>
                        ) : (
                          s.title
                        )}
                        {s.originalArtist && <span className='request-artist'> — {s.originalArtist}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      </main>
    </>
  );
}

export default RequestsPage;
